import { MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { useBasket } from '../../../domains/basket'
import { CollectionSlug } from '../../../domains/collections'
import { VisibleAttribute } from './RecipeAttribute'
import { detailedRecipePriorityOrder } from './config'

type AttributeConfigInGrid = {
  name: VisibleAttribute
  value: string | number | boolean
  icon: string
  show: boolean
}

export const useAttributes = ({
  maxNoAttributes,
  recipe,
}: {
  maxNoAttributes?: number
  recipe: MenuRecipe
}) => {
  const { numPortions } = useBasket()

  if (!recipe) {
    return []
  }

  const cookingTime: number | null = recipe.prep_time as number

  const useWithin = recipe.shelf_life
    ? `${recipe.shelf_life?.min_days}-${recipe.shelf_life?.max_days}`
    : ''
  const fiveADay = recipe.five_a_day as number
  const diet = recipe.diet_type?.slug as string
  const cals = recipe.nutritional_information?.per_portion?.energy_kcal as number
  const cuisine = recipe.cuisine?.name as string
  const isChefPrepared = recipe.is_chef_prepared as boolean

  const dietaryTagSlugs: string[] =
    recipe.dietary_claims?.map((dietaryClaim) => dietaryClaim.slug) || []
  const dairyFree = dietaryTagSlugs.some((slug) => slug === CollectionSlug.DairyFree)
  const glutenFree = dietaryTagSlugs.some((slug) => slug === CollectionSlug.GlutenFree)

  const attributes: AttributeConfigInGrid[] = [
    {
      name: 'cookingTime',
      value: cookingTime || 0,
      icon: 'icon-time',
      show: !isChefPrepared && Boolean(cookingTime),
    },
    {
      name: 'useWithin',
      value: useWithin,
      icon: 'icon-use-within',
      show: Boolean(useWithin),
    },
    {
      name: 'diet',
      value: diet,
      icon: 'icon-diet',
      show: Boolean(diet) && ['vegetarian', 'vegan'].includes(diet.toLowerCase()),
    },
    { name: 'fiveADay', value: fiveADay, icon: 'icon-five-a-day', show: fiveADay > 0 },
    { name: 'cals', value: cals || '', icon: 'icon-calories', show: Boolean(cals) },
    { name: 'cuisine', value: cuisine || '', icon: 'icon-cuisine', show: Boolean(cuisine) },
    { name: 'glutenFree', value: glutenFree, icon: 'icon-gluten-free', show: Boolean(glutenFree) },
    { name: 'dairyFree', value: dairyFree, icon: 'icon-dairy-free', show: Boolean(dairyFree) },
    {
      name: 'numPortions',
      value: numPortions || 0,
      icon: 'icon-servings',
      show: Boolean(isChefPrepared) && Boolean(numPortions),
    },
  ]

  const attributesInPriorityOrder = detailedRecipePriorityOrder.reduce(
    (acc: AttributeConfigInGrid[], attributeName) => {
      const anAttribute = attributes.find(({ name }) => name === attributeName)

      return anAttribute ? [...acc, anAttribute] : acc
    },
    [],
  )

  return attributesInPriorityOrder.filter(({ show }) => show !== false).slice(0, maxNoAttributes)
}
